import type { DocumentsState } from '@platform/documents';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  DisconnectResponse,
  GetProviderConnectionsRequest,
  GetProviderConnectionsResponse,
  GetSlackConnectionsResponse,
  GoogleConnectionStatus,
  OAuthStateRequest,
  OAuthStateResponse,
  RevokeProviderOAuthRequest,
  SlackOAuthRequest,
  SlackOAuthResponse,
} from '@shared/types/integrations';
import type { PersonRoleListResponse, RbacRole } from '@shared/types/rbac';
import type { ApiUserShowResponseDto } from '@shared/types/user';
import { appendTelemetryHeaders } from '../../services/telemetry';
import { tokenService } from '../../services/tokenService';
import { withTransientQueryRetry } from './baseQueryRetry';
import { handleImpersonationAuthError } from './impersonationErrorHandler';

// Identity service endpoints are served from the same origin as the frontend
// No need for separate base URL configuration

// Define RootState type here to avoid circular dependency
export interface AuthState {
  isAuthenticated: boolean;
  status: 'unauthenticated' | 'authenticated' | 'expired';
  token: string | null;
  isUserResolved: boolean;
  user: {
    id: string;
    email: string;
    organizationId: string;
    roles: string[];
    permissions: string[];
    permissionsHash?: string;
  } | null;
  isLoading: boolean;
  error: string | null;
  sessionMessage: string | null;
  appUserId?: number | null;
}

export interface ImpersonationState {
  active: boolean;
  targetId: string;
  targetName: string;
  targetEmail: string;
  targetOrgId: string;
  targetRoles: string[];
  targetPermissions: string[];
  returnPath?: string;
  token: string;
  expiresAt: string;
  originalToken: string;
  originalUser: AuthState['user'];
}

export interface RootState {
  auth: AuthState & { impersonation: ImpersonationState | null };
  api: unknown;
  timeline?: {
    byConvId: Record<string, { byId: Record<string, any>; order: string[] }>;
    activeConvId: string | null;
  };
  documents: DocumentsState;
}
export const prepareApiHeaders = async (headers: Headers): Promise<Headers> => {
  // Use getActiveToken which returns impersonation token if active
  const token = await tokenService.getActiveToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  appendTelemetryHeaders(headers);
  return headers;
};

const rawBaseQuery = withTransientQueryRetry(
  fetchBaseQuery({
    baseUrl: '/api/v1',
    prepareHeaders: prepareApiHeaders,
  })
);

const authAwareBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  const status = result.error?.status;

  // Parse JSON error responses if data is a string
  if (result.error && typeof result.error.data === 'string') {
    try {
      const parsed = JSON.parse(result.error.data);
      result.error.data = parsed;
    } catch {
      // If parsing fails, leave data as string
    }
  }

  if (typeof status === 'number' && [401, 419].includes(status)) {
    // Check if this is a permission error vs authentication error
    const errorData = result.error?.data as any;
    const state = api.getState() as RootState;
    const hasToken = !!(state.auth.token || (await tokenService.getAccessToken()));

    // Safely extract error message and code, handling both string and object types
    // Prioritize message over error since message contains more descriptive details
    const errorMessage =
      typeof errorData?.message === 'string'
        ? errorData.message
        : typeof errorData?.error === 'string'
          ? errorData.error
          : '';
    const errorCode =
      typeof errorData?.code === 'string'
        ? errorData.code
        : typeof errorData?.error === 'string'
          ? errorData.error
          : '';

    // Permission/authorization errors should NOT trigger logout
    // Treat as permission error if:
    // 1. User has a token (is authenticated) AND
    // 2. Error code is 'unauthorized' AND
    // 3. Error message explicitly indicates authorization/permission issue
    //    (not token expiration or authentication failure)
    // This prevents clearing tokens when user is authenticated but lacks permissions
    // Note: We must check the error message even for admin endpoints to distinguish
    // between "token expired" (should logout) and "permission denied" (should not logout)
    const isPermissionError =
      hasToken &&
      errorCode === 'unauthorized' &&
      typeof errorMessage === 'string' &&
      (errorMessage.includes('authorization required') ||
        errorMessage.includes('insufficient_permissions') ||
        errorMessage.includes('Admin role required') ||
        errorMessage.includes('permission denied') ||
        errorMessage.includes('access denied') ||
        errorMessage.includes('forbidden'));

    // Only clear tokens if it's a true authentication error, not a permission error
    if (!isPermissionError) {
      // Check if we're impersonating - restore admin session instead of full logout
      const wasImpersonationRestored = handleImpersonationAuthError(api as any, () => {
        // Reset both API caches - import rpcSlice lazily to avoid circular deps
        api.dispatch(apiSlice.util.resetApiState());
        // Use dynamic import pattern to avoid circular dependency
        import('./rpcSlice').then(({ rpcSlice }) => {
          api.dispatch(rpcSlice.util.resetApiState());
        });
      });

      if (!wasImpersonationRestored) {
        // Normal session expiry - clear everything
        tokenService.clearTokens({
          expired: true,
          message: 'Your session has expired. Please sign in again.',
        });
      }
    }
    // If it's a permission error, let it bubble up to the UI for proper error handling
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: authAwareBaseQuery,
  tagTypes: [
    'User',
    'Connection',
    'PersonContext',
    'PersonRoles',
    'AdminRoles',
    'TeamDiscovery',
    'CronJobs',
    'CronRuns',
    'Coaches',
    'Members',
    'CoachDetail',
    'CoachMember',
    'Emails',
    'ViewState',
    'AdminUserViewStates',
    'FeatureFlags',
    'FeatureFlag',
    'FeatureFlagCoaches',
    'FeatureFlagPeople',
    'OnboardingSubmissions',
    'OnboardingSubmission',
    'OnboardingForms',
    'FormMappings',
    'AutoSummaryGlobalStatus',
    'EvalSuites',
    'EvalSuite',
    'EvalCase',
    'EvalFixtures',
    'EvalRuns',
    'EvalRun',
    'InternalApps',
    'CalendarSettings',
    'Sessions',
    'Appointments',
    'CalendarChanges',
    'SessionArtifactGeneration',
  ],
  endpoints: (builder) => ({
    // Team Discovery: Get existing team members
    getTeam: builder.query<GetTeamResponse, void>({
      query: () => '/coach/team',
      providesTags: ['TeamDiscovery'],
    }),
    // Current app user (users/me)
    getMe: builder.query<ApiUserShowResponseDto, string | null>({
      // The identity argument is a cache key only; authorization still comes
      // exclusively from the active bearer token in prepareApiHeaders.
      query: () => `/users/me`,
      providesTags: ['User'],
    }),

    // Identity server endpoints
    startProviderOAuth: builder.mutation<OAuthStateResponse, OAuthStateRequest>({
      query: ({ provider, scopes, redirect }) => ({
        url: '/oauth/state',
        method: 'POST',
        body: { provider, scopes, redirect },
      }),
    }),
    startSlackOAuth: builder.mutation<SlackOAuthResponse, SlackOAuthRequest>({
      query: ({ personId, redirect_url }) => ({
        url: '/oauth/state',
        method: 'POST',
        body: {
          provider: 'slack',
          person_id: personId,
          redirect: redirect_url,
        },
      }),
    }),
    startGitHubOAuth: builder.mutation<OAuthStateResponse, OAuthStateRequest>({
      query: ({ scopes, redirect, personId, orgId }) => ({
        url: '/oauth/state/github',
        method: 'POST',
        body: {
          provider: 'github',
          scopes,
          redirect,
          person_id: personId,
          org_id: orgId,
        },
      }),
    }),
    startLinearOAuth: builder.mutation<OAuthStateResponse, OAuthStateRequest>({
      query: ({ scopes, redirect, personId, orgId }) => ({
        url: '/oauth/state/linear',
        method: 'POST',
        body: {
          provider: 'linear',
          scopes,
          redirect,
          person_id: personId,
          org_id: orgId,
        },
      }),
    }),
    getGoogleConnectionStatus: builder.query<GoogleConnectionStatus, void>({
      query: () => '/google/connections/me',
      providesTags: ['Connection'],
    }),
    disconnectGoogle: builder.mutation<{ ok: boolean }, { revoke?: boolean }>({
      query: (body) => ({
        url: '/google/disconnect',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Connection'],
    }),

    // Google Drive: list files (requires auth)
    getDriveFiles: builder.query<{ items: DriveFile[]; summary?: any }, void>({
      query: () => '/google-drive/files',
    }),

    // Google Calendar: today's events (requires auth)
    getCalendarEventsToday: builder.query<{ items: CalendarEvent[]; summary?: any }, void>({
      query: () => '/google-calendar/events/today',
    }),

    // Slack: list connections for a person
    getSlackConnections: builder.query<GetSlackConnectionsResponse, { personId: string }>({
      query: ({ personId }) => `/connections/slack?person_id=${encodeURIComponent(personId)}`,
      providesTags: ['Connection'],
    }),

    // Slack: disconnect/revoke (optionally by workspace)
    disconnectSlack: builder.mutation<
      { revoked: boolean; provider: string; count?: number },
      { personId: string; workspaceId?: string; reason?: string }
    >({
      query: ({ personId, workspaceId, reason }) => ({
        url: '/oauth/revoke',
        method: 'POST',
        body: {
          person_id: personId,
          provider: 'slack',
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
          ...(reason ? { reason } : {}),
        },
      }),
      invalidatesTags: ['Connection'],
    }),

    // Generic provider connections (e.g., google-calendar, google-drive)
    getProviderConnections: builder.query<
      GetProviderConnectionsResponse,
      GetProviderConnectionsRequest
    >({
      query: ({ provider, personId }) =>
        `/connections/${provider}?person_id=${encodeURIComponent(personId)}`,
      providesTags: ['Connection'],
    }),

    // Revoke OAuth for a specific provider for the current person
    revokeProviderOAuth: builder.mutation<DisconnectResponse, RevokeProviderOAuthRequest>({
      query: ({ personId, provider }) => ({
        url: '/oauth/revoke',
        method: 'POST',
        body: { person_id: personId, provider },
      }),
      invalidatesTags: ['Connection'],
    }),

    // Admin: Get specific prompt
    getPrompt: builder.query<PromptMetadata, string>({
      query: (key) => `/admin/prompts/${key}`,
    }),

    // Admin: Update prompt
    updatePrompt: builder.mutation<PromptMetadata, { key: string; content: string }>({
      query: ({ key, content }) => ({
        url: `/admin/prompts/${key}`,
        method: 'PUT',
        body: { content },
      }),
    }),

    // Admin: Reset prompt to default
    resetPrompt: builder.mutation<PromptMetadata, string>({
      query: (key) => ({
        url: `/admin/prompts/${key}/reset`,
        method: 'POST',
      }),
    }),

    // List organizations (for authenticated users)
    listOrganizations: builder.query<Organization[], { limit?: number; cursor?: string }>({
      query: ({ limit, cursor }) => ({
        url: '/auth/organizations',
        params: {
          ...(limit ? { limit } : {}),
          ...(cursor ? { cursor } : {}),
        },
      }),
      transformResponse: (response: any) => {
        // Handle different possible response structures from Stytch
        const orgs = response.organizations || response.results || [];
        return orgs
          .map((org: any) => ({
            id: org.organization_id || org.id,
            name:
              org.organization_name ||
              org.name ||
              org.organization_slug ||
              org.slug ||
              org.organization_id ||
              org.id,
            slug: org.organization_slug || org.slug,
          }))
          .sort((a: Organization, b: Organization) => a.name.localeCompare(b.name)); // Sort by name
      },
    }),

    // List all people (for admin users)
    listAllPeople: builder.query<Person[], { limit?: number }>({
      query: ({ limit }) => ({
        url: '/auth/admin/people',
        params: {
          ...(limit ? { limit } : {}),
        },
      }),
      transformResponse: (response: any) => {
        const people = response.people || [];
        return people.map((person: any) => ({
          id: person.id,
          email: person.email,
          givenName: person.given_name || '',
          familyName: person.family_name || '',
        }));
      },
    }),

    // Admin RBAC: list roles
    getAdminRoles: builder.query<{ roles: RbacRole[] }, void>({
      query: () => '/admin/rbac/roles',
      providesTags: ['AdminRoles'],
    }),

    // Admin RBAC: list people with roles
    getAdminPersonRoles: builder.query<
      PersonRoleListResponse,
      { q?: string; orgId?: string; page?: number; per?: number } | undefined
    >({
      query: (params) => ({
        url: '/admin/rbac/person_roles',
        params: {
          ...(params?.q ? { q: params.q } : {}),
          ...(params?.orgId ? { org_id: params.orgId } : {}),
          ...(params?.page ? { page: params.page } : {}),
          ...(params?.per ? { per: params.per } : {}),
        },
      }),
      providesTags: ['PersonRoles'],
    }),

    // Admin RBAC: assign role to person
    assignPersonRole: builder.mutation<
      { ok: boolean },
      { personId: string; roleName: string; orgId?: string | null }
    >({
      query: ({ personId, roleName, orgId }) => ({
        url: '/admin/rbac/person_roles',
        method: 'POST',
        body: {
          person_id: personId,
          role_name: roleName,
          org_id: orgId || undefined,
        },
      }),
      invalidatesTags: ['PersonRoles'],
    }),

    // Admin RBAC: remove role from person
    unassignPersonRole: builder.mutation<
      { ok: boolean },
      { personId: string; roleName: string; orgId?: string | null }
    >({
      query: ({ personId, roleName, orgId }) => ({
        url: '/admin/rbac/person_roles',
        method: 'DELETE',
        body: {
          person_id: personId,
          role_name: roleName,
          org_id: orgId || undefined,
        },
      }),
      invalidatesTags: ['PersonRoles'],
    }),

    // Admin RBAC: bust cache for a person (or all if no personId)
    bustRbacCache: builder.mutation<
      { ok: boolean; message: string; person_id?: string; org_id?: string },
      { personId?: string; orgId?: string }
    >({
      query: ({ personId, orgId }) => ({
        url: '/admin/rbac/cache/bust',
        method: 'POST',
        body: {
          person_id: personId || undefined,
          org_id: orgId || undefined,
        },
      }),
      // Invalidate PersonRoles to refresh any displayed role data
      invalidatesTags: ['PersonRoles'],
    }),
  }),
});

export const {
  useGetMeQuery,
  useGetTeamQuery,
  useStartProviderOAuthMutation,
  useStartSlackOAuthMutation,
  useStartGitHubOAuthMutation,
  useStartLinearOAuthMutation,
  useGetGoogleConnectionStatusQuery,
  useDisconnectGoogleMutation,
  useGetDriveFilesQuery,
  useGetCalendarEventsTodayQuery,
  useGetSlackConnectionsQuery,
  useGetProviderConnectionsQuery,
  useRevokeProviderOAuthMutation,
  useDisconnectSlackMutation,
  useGetPromptQuery,
  useUpdatePromptMutation,
  useResetPromptMutation,
  useListOrganizationsQuery,
  useListAllPeopleQuery,
  useGetAdminRolesQuery,
  useGetAdminPersonRolesQuery,
  useAssignPersonRoleMutation,
  useUnassignPersonRoleMutation,
  useBustRbacCacheMutation,
} = apiSlice;

export const useStartGoogleOAuthMutation = useStartProviderOAuthMutation;

// Types
// Slack connections listing shape (from identity service)
export interface SlackConnectionOut {
  id: string;
  provider: string;
  provider_user_id?: string | null;
  team_id?: string | null;
  status: string;
  metadata: Record<string, any>;
  connected: boolean;
  has_user_token: boolean;
  has_bot_token: boolean;
  scopes_union: string[];
  updated_at: string;
}

export interface ProviderConnectionOut {
  id: string;
  provider: string;
  provider_user_id?: string | null;
  team_id?: string | null;
  status: string;
  metadata: Record<string, any>;
  connected: boolean;
  has_user_token: boolean;
  has_bot_token: boolean;
  scopes_union: string[];
  updated_at: string;
}

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
}

export interface CalendarEvent {
  id: string;
  summary?: string;
  start?: { dateTime?: string; date?: string };
  end?: { dateTime?: string; date?: string };
  status?: string;
  description?: string;
  location?: string;
}

export interface PersonContextRecord {
  person_id: string;
  context: string;
  created_at: string;
  updated_at: string;
}

export interface PromptMetadata {
  key: string;
  content: string;
  isDefault: boolean;
  updatedAt: string;
}

// Organization types
export interface Organization {
  id: string;
  name: string;
  slug?: string;
}

// Person types
export interface Person {
  id: string;
  email: string;
  givenName: string;
  familyName: string;
}

// Note: Stytch organizations response structure may vary
// The transformResponse handles multiple possible formats

// Team Discovery types

// Team member from GET /api/v1/coach/team
export interface TeamMember {
  person: {
    id: string;
    primary_email: string;
    given_name?: string;
    family_name?: string;
    org_id?: string;
    picture_url?: string;
  };
  session_count: number;
  active: boolean;
  relationship_type?: 'active' | 'active_chemistry_only' | 'past_member' | 'past_chemistry_call';
  chemistry_call_at?: string;
  org_name?: string;
}

export interface GetTeamResponse {
  members: TeamMember[];
}
